import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const CareersPage = () => {
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [selectedLocation, setSelectedLocation] = useState('all');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const positions = [
    {
      id: 1,
      title: "Senior Quantum Field Engineer",
      department: "engineering",
      location: "Nairobi, Kenya",
      type: "Full-time",
      level: "Senior",
      description: "Lead the development of our electromagnetic field manipulation hardware and quantum coherence systems."
    },
    {
      id: 2,
      title: "Continuous AI Research Scientist",
      department: "research",
      location: "Remote",
      type: "Full-time",
      level: "Senior",
      description: "Pioneer new approaches to continuous intelligence using electromagnetic field computation."
    },
    {
      id: 3,
      title: "EADC Platform Engineer",
      department: "engineering",
      location: "San Francisco, USA",
      type: "Full-time",
      level: "Mid-level",
      description: "Build and scale our revolutionary computing platform infrastructure."
    },
    {
      id: 4,
      title: "Mathematical Physics Researcher",
      department: "research",
      location: "Cambridge, UK",
      type: "Full-time",
      level: "Senior",
      description: "Advance the theoretical foundations of electromagnetic computing."
    },
    {
      id: 5,
      title: "Developer Advocate",
      department: "developer-relations",
      location: "Remote",
      type: "Full-time",
      level: "Mid-level",
      description: "Help developers worldwide adopt and succeed with EADC technology."
    },
    {
      id: 6,
      title: "Strategic Partnerships Manager",
      department: "business",
      location: "Lagos, Nigeria",
      type: "Full-time",
      level: "Senior",
      description: "Build relationships with African institutions and accelerate technology adoption."
    }
  ];

  const departments = [
    { value: 'all', label: 'All Departments' },
    { value: 'engineering', label: 'Engineering' },
    { value: 'research', label: 'Research' },
    { value: 'developer-relations', label: 'Developer Relations' },
    { value: 'business', label: 'Business' }
  ];

  const locations = [
    { value: 'all', label: 'All Locations' },
    { value: 'Nairobi, Kenya', label: 'Nairobi' },
    { value: 'San Francisco, USA', label: 'San Francisco' },
    { value: 'Cambridge, UK', label: 'Cambridge' },
    { value: 'Lagos, Nigeria', label: 'Lagos' },
    { value: 'Remote', label: 'Remote' }
  ];

  const filteredPositions = positions.filter(position => {
    const departmentMatch = selectedDepartment === 'all' || position.department === selectedDepartment;
    const locationMatch = selectedLocation === 'all' || position.location === selectedLocation;
    return departmentMatch && locationMatch;
  });

  const benefits = [
    {
      icon: "🌍",
      title: "Global Impact",
      description: "Work on technology that will transform computation and solve humanity's greatest challenges"
    },
    {
      icon: "🚀",
      title: "Cutting-Edge Research",
      description: "Push the boundaries of physics and computer science with world-class researchers"
    },
    {
      icon: "💰",
      title: "Competitive Compensation",
      description: "Top-tier salary, equity, and comprehensive benefits package"
    },
    {
      icon: "🌴",
      title: "Work-Life Balance",
      description: "Flexible hours, unlimited PTO, and support for remote work"
    },
    {
      icon: "📚",
      title: "Learning & Development",
      description: "Conference attendance, research sabbaticals, and continuous education support"
    },
    {
      icon: "🤝",
      title: "Inclusive Culture",
      description: "Diverse, collaborative environment where every voice matters"
    }
  ];

  return (
    <>
      <style>{`
        .careers-hero {
          padding: 10rem 2rem 4rem;
          background: linear-gradient(135deg, var(--blue-600), var(--purple-600));
          color: var(--white);
          text-align: center;
          position: relative;
          overflow: hidden;
        }

        .careers-hero::before {
          content: '';
          position: absolute;
          top: -50%;
          left: -50%;
          width: 200%;
          height: 200%;
          background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
          animation: rotate 30s linear infinite;
        }

        @keyframes rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .careers-hero-content {
          max-width: 1280px;
          margin: 0 auto;
          position: relative;
          z-index: 1;
        }

        .careers-hero-title {
          font-size: clamp(3rem, 6vw, 4.5rem);
          font-weight: 800;
          margin-bottom: 1.5rem;
          letter-spacing: -0.05em;
        }

        .careers-hero-description {
          font-size: 1.375rem;
          line-height: 1.6;
          max-width: 800px;
          margin: 0 auto 3rem;
          opacity: 0.9;
        }

        .careers-stats {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
          gap: 2rem;
          max-width: 600px;
          margin: 0 auto;
        }

        .career-stat {
          text-align: center;
        }

        .career-stat-number {
          font-size: 2.5rem;
          font-weight: 800;
          margin-bottom: 0.5rem;
        }

        .career-stat-label {
          font-size: 1rem;
          opacity: 0.8;
        }

        .positions-section {
          padding: 6rem 2rem;
          background: var(--white);
        }

        .positions-container {
          max-width: 1280px;
          margin: 0 auto;
        }

        .positions-header {
          text-align: center;
          margin-bottom: 4rem;
        }

        .positions-title {
          font-size: 2.5rem;
          font-weight: 800;
          color: var(--gray-900);
          margin-bottom: 1rem;
        }

        .filters-container {
          display: flex;
          gap: 1rem;
          margin-bottom: 3rem;
          flex-wrap: wrap;
        }

        .filter-select {
          padding: 0.75rem 1.5rem;
          border: 1px solid var(--gray-300);
          border-radius: var(--radius);
          background: var(--white);
          font-size: 0.95rem;
          color: var(--gray-700);
          cursor: pointer;
          transition: all var(--transition-fast);
        }

        .filter-select:hover {
          border-color: var(--gray-400);
        }

        .filter-select:focus {
          outline: none;
          border-color: var(--blue-500);
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .positions-grid {
          display: grid;
          gap: 1.5rem;
        }

        .position-card {
          background: var(--white);
          border: 1px solid var(--gray-200);
          border-radius: var(--radius-lg);
          padding: 2rem;
          transition: all var(--transition-base);
          cursor: pointer;
        }

        .position-card:hover {
          box-shadow: var(--shadow-lg);
          transform: translateY(-2px);
          border-color: var(--blue-200);
        }

        .position-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 1rem;
        }

        .position-title {
          font-size: 1.375rem;
          font-weight: 700;
          color: var(--gray-900);
          margin-bottom: 0.5rem;
        }

        .position-meta {
          display: flex;
          gap: 1rem;
          flex-wrap: wrap;
          margin-bottom: 1rem;
        }

        .position-tag {
          padding: 0.375rem 0.75rem;
          background: var(--gray-100);
          border-radius: 50px;
          font-size: 0.875rem;
          color: var(--gray-700);
        }

        .position-tag.department {
          background: var(--blue-100);
          color: var(--blue-700);
        }

        .position-description {
          color: var(--gray-600);
          line-height: 1.6;
          margin-bottom: 1.5rem;
        }

        .apply-button {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.5rem;
          background: var(--black);
          color: var(--white);
          border-radius: var(--radius);
          text-decoration: none;
          font-weight: 600;
          transition: all var(--transition-fast);
        }

        .apply-button:hover {
          background: var(--gray-800);
          transform: translateY(-1px);
        }

        .benefits-section {
          padding: 6rem 2rem;
          background: var(--gray-50);
        }

        .benefits-container {
          max-width: 1280px;
          margin: 0 auto;
        }

        .benefits-header {
          text-align: center;
          margin-bottom: 4rem;
        }

        .benefits-title {
          font-size: 2.5rem;
          font-weight: 800;
          color: var(--gray-900);
          margin-bottom: 1rem;
        }

        .benefits-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 2rem;
        }

        .benefit-card {
          background: var(--white);
          border-radius: var(--radius-lg);
          padding: 2rem;
          text-align: center;
          transition: all var(--transition-base);
        }

        .benefit-card:hover {
          transform: translateY(-4px);
          box-shadow: var(--shadow-md);
        }

        .benefit-icon {
          font-size: 3rem;
          margin-bottom: 1rem;
        }

        .benefit-title {
          font-size: 1.25rem;
          font-weight: 700;
          color: var(--gray-900);
          margin-bottom: 0.5rem;
        }

        .benefit-description {
          color: var(--gray-600);
          line-height: 1.6;
        }

        .culture-section {
          padding: 6rem 2rem;
          background: var(--white);
        }

        .culture-container {
          max-width: 1000px;
          margin: 0 auto;
          text-align: center;
        }

        .culture-title {
          font-size: 2.5rem;
          font-weight: 800;
          color: var(--gray-900);
          margin-bottom: 2rem;
        }

        .culture-quote {
          font-size: 1.5rem;
          font-style: italic;
          color: var(--gray-700);
          line-height: 1.6;
          margin-bottom: 2rem;
        }

        .culture-attribution {
          font-size: 1rem;
          color: var(--gray-500);
          font-weight: 600;
        }

        .cta-section {
          padding: 6rem 2rem;
          background: var(--gray-900);
          color: var(--white);
          text-align: center;
        }

        .cta-title {
          font-size: 2.5rem;
          font-weight: 800;
          margin-bottom: 1rem;
        }

        .cta-description {
          font-size: 1.25rem;
          margin-bottom: 2rem;
          opacity: 0.9;
        }

        .cta-email {
          color: var(--blue-400);
          text-decoration: none;
          font-weight: 600;
          font-size: 1.125rem;
          transition: all var(--transition-fast);
        }

        .cta-email:hover {
          color: var(--blue-300);
        }

        @media (max-width: 768px) {
          .careers-hero {
            padding: 8rem 1rem 3rem;
          }

          .careers-stats {
            grid-template-columns: repeat(2, 1fr);
          }

          .filters-container {
            flex-direction: column;
          }

          .filter-select {
            width: 100%;
          }

          .position-header {
            flex-direction: column;
            gap: 1rem;
          }

          .benefits-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      <section className="careers-hero">
        <div className="careers-hero-content">
          <h1 className="careers-hero-title">
            Join the Computational Revolution
          </h1>
          <p className="careers-hero-description">
            Be part of a team that's redefining the fundamental limits of computation. We're looking for exceptional individuals who share our vision of transcending digital constraints.
          </p>
          <div className="careers-stats">
            <div className="career-stat">
              <div className="career-stat-number">47</div>
              <div className="career-stat-label">Team Members</div>
            </div>
            <div className="career-stat">
              <div className="career-stat-number">12</div>
              <div className="career-stat-label">Countries</div>
            </div>
            <div className="career-stat">
              <div className="career-stat-number">{filteredPositions.length}</div>
              <div className="career-stat-label">Open Positions</div>
            </div>
            <div className="career-stat">
              <div className="career-stat-number">∞</div>
              <div className="career-stat-label">Possibilities</div>
            </div>
          </div>
        </div>
      </section>

      <section className="positions-section">
        <div className="positions-container">
          <div className="positions-header">
            <h2 className="positions-title">Open Positions</h2>
          </div>

          <div className="filters-container">
            <select 
              className="filter-select"
              value={selectedDepartment}
              onChange={(e) => setSelectedDepartment(e.target.value)}
            >
              {departments.map(dept => (
                <option key={dept.value} value={dept.value}>{dept.label}</option>
              ))}
            </select>

            <select 
              className="filter-select"
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
            >
              {locations.map(loc => (
                <option key={loc.value} value={loc.value}>{loc.label}</option>
              ))}
            </select>
          </div>

          <div className="positions-grid">
            {filteredPositions.map(position => (
              <div key={position.id} className="position-card">
                <div className="position-header">
                  <div>
                    <h3 className="position-title">{position.title}</h3>
                    <div className="position-meta">
                      <span className="position-tag department">{position.department}</span>
                      <span className="position-tag">{position.location}</span>
                      <span className="position-tag">{position.type}</span>
                      <span className="position-tag">{position.level}</span>
                    </div>
                  </div>
                </div>
                <p className="position-description">{position.description}</p>
                <Link to={`/careers/${position.id}`} className="apply-button">
                  Apply Now
                  <span>→</span>
                </Link>
              </div>
            ))}
          </div>

          {filteredPositions.length === 0 && (
            <div style={{ textAlign: 'center', padding: '4rem 2rem' }}>
              <p style={{ fontSize: '1.125rem', color: 'var(--gray-600)' }}>
                No positions match your filters. Try adjusting your selection.
              </p>
            </div>
          )}
        </div>
      </section>

      <section className="benefits-section">
        <div className="benefits-container">
          <div className="benefits-header">
            <h2 className="benefits-title">Why Angalifu?</h2>
          </div>
          <div className="benefits-grid">
            {benefits.map((benefit, index) => (
              <div key={index} className="benefit-card">
                <div className="benefit-icon">{benefit.icon}</div>
                <h3 className="benefit-title">{benefit.title}</h3>
                <p className="benefit-description">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="culture-section">
        <div className="culture-container">
          <h2 className="culture-title">Our Culture</h2>
          <p className="culture-quote">
            "We don't just push boundaries—we dissolve them. At Angalifu, impossible is not a wall, it's a door waiting to be opened."
          </p>
          <p className="culture-attribution">— Dr. Amara Okafor, CEO & Co-Founder</p>
        </div>
      </section>

      <section className="cta-section">
        <h2 className="cta-title">Don't See Your Role?</h2>
        <p className="cta-description">
          We're always looking for exceptional talent. Send us your resume and tell us how you can contribute to the future of computation.
        </p>
        <a href="mailto:careers@angalifu.com" className="cta-email">
          careers@angalifu.com
        </a>
      </section>
    </>
  );
};

export default CareersPage;
